import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, Modal, PageHeader, EmptyState, Badge } from "@/components/kit";
import InterestPicker from "@/components/InterestPicker";
import { TID } from "@/constants/testIds";
import { Plus } from "@phosphor-icons/react";

const TYPES = [
  { value: "deposit", label: "Deposit-backed" },
  { value: "general", label: "General" },
  { value: "commodity", label: "Commodity" },
];

export default function LoansPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const customerIdFilter = params.get("customer_id");
  const [loans, setLoans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [savings, setSavings] = useState([]);
  const [loanType, setLoanType] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ loan_type: "general", interest_method: "SI", interest_rate: 12, tenure_days: 120 });
  const [err, setErr] = useState("");

  const load = async () => {
    const q = new URLSearchParams();
    if (customerIdFilter) q.set("customer_id", customerIdFilter);
    if (loanType) q.set("loan_type", loanType);
    const [l, c, s] = await Promise.all([
      api.get(`/loans?${q.toString()}`),
      api.get("/customers"),
      api.get("/savings"),
    ]);
    setLoans(l.data);
    setCustomers(c.data);
    setSavings(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [loanType, customerIdFilter]);

  const canCreate = ["super_admin", "branch_admin", "staff"].includes(user.role);
  const custName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const eligibleSavings = form.customer_id ? savings.filter(s => s.customer_id === form.customer_id && s.product === "fd") : [];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const body = { ...form };
    ["principal", "interest_rate", "tenure_days", "commodity_value", "file_charge_value"].forEach(k => {
      if (body[k] !== undefined && body[k] !== "") body[k] = Number(body[k]);
      else delete body[k];
    });
    if (!body.file_charge_type) delete body.file_charge_type;
    if (!body.disbursed_on) delete body.disbursed_on;
    try {
      await api.post("/loans", body);
      setOpen(false);
      setForm({ loan_type: "general", interest_method: "SI", interest_rate: 12, tenure_days: 120 });
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <PageHeader
        title="Loans"
        subtitle="Deposit-backed, general, and commodity loans with full EMI tracking."
        action={
          canCreate && (
            <Button onClick={() => setOpen(true)} data-testid={TID.addBtn("loan")}>
              <Plus size={16} weight="bold" /> New loan
            </Button>
          )
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex gap-2 flex-wrap">
          {[["", "All"], ...TYPES.map(t => [t.value, t.label])].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setLoanType(v)}
              data-testid={`loans-filter-${v || "all"}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                loanType === v
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        {loans.length === 0 ? (
          <EmptyState title="No loans yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 font-semibold">Type</th>
                  <th className="text-right px-4 py-3 font-semibold">Principal</th>
                  <th className="text-right px-4 py-3 font-semibold">EMI (daily)</th>
                  <th className="text-right px-4 py-3 font-semibold">Outstanding</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loans.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`loan-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{custName(l.customer_id)}</div>
                      {l.loan_no && <div className="text-xs text-slate-500 mono">{l.loan_no}</div>}
                    </td>
                    <td className="px-4 py-3"><Badge tone="info">{typeLabel(l.loan_type)}</Badge></td>
                    <td className="px-4 py-3 mono text-right">{money(l.principal)}</td>
                    <td className="px-4 py-3 mono text-right">{money(l.emi)}</td>
                    <td className="px-4 py-3 mono text-right font-medium">{money(l.outstanding)}</td>
                    <td className="px-4 py-3">
                      {l.status === "closed" ? <Badge tone="success">Closed</Badge> : (
                        l.overdue_count > 0 ? <Badge tone="danger">{l.overdue_count} overdue</Badge> : <Badge tone="neutral">Active</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/loans/${l.id}`} className="text-emerald-700 font-medium hover:underline" data-testid={`open-loan-${l.id}`}>Open →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="New loan" maxWidth="max-w-xl">
        <form onSubmit={submit} className="space-y-4">
          <Select label="Customer" required testId="loan-customer" value={form.customer_id || ""} onChange={async (e) => {
            const cid = e.target.value;
            setForm((prev) => ({ ...prev, customer_id: cid }));
            if (cid) {
              try {
                const { data } = await api.get(`/customers/${cid}/suggest-loan-no`);
                setForm((prev) => ({ ...prev, customer_id: cid, loan_no: prev.loan_no || data.loan_no }));
              } catch { /* ignore */ }
            }
          }}>
            <option value="">Select customer</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_code ? `${c.customer_code} · ` : ""}{c.name} · {c.phone}</option>)}
          </Select>
          <Input label="Loan number (management-assigned)" required testId="loan-no"
            placeholder="e.g. CUS-000001-L1 (auto-suggested — edit if you follow a different series)"
            value={form.loan_no || ""} onChange={(e) => setForm({ ...form, loan_no: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Loan type" required testId="loan-type" value={form.loan_type} onChange={(e) => setForm({ ...form, loan_type: e.target.value })}>
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
            <Input label="Tenure (days)" type="number" required testId="loan-tenure"
              value={form.tenure_days} onChange={(e) => setForm({ ...form, tenure_days: e.target.value })} />
          </div>
          <Input label="Principal" type="number" step="0.01" required testId="loan-principal"
            value={form.principal || ""} onChange={(e) => setForm({ ...form, principal: e.target.value })} />

          <InterestPicker
            value={{ method: form.interest_method, rate: form.interest_rate }}
            onChange={(v) => setForm({ ...form, interest_method: v.method, interest_rate: v.rate })}
            previewKind="loan"
            previewParams={{ principal: form.principal, tenure_days: form.tenure_days }}
            showLoanNote
          />
          {form.loan_type === "deposit" && (
            <Select label="Linked FD (savings)" required testId="loan-linked-fd" value={form.linked_savings_id || ""} onChange={(e) => setForm({ ...form, linked_savings_id: e.target.value })}>
              <option value="">Select FD account</option>
              {eligibleSavings.map(s => <option key={s.id} value={s.id}>FD #{s.id.slice(0,8)} · {money(s.principal || 0)}</option>)}
            </Select>
          )}
          {form.loan_type === "commodity" && (
            <div className="grid grid-cols-2 gap-3">
              <Input label="Commodity name" required testId="loan-commodity-name"
                value={form.commodity_name || ""} onChange={(e) => setForm({ ...form, commodity_name: e.target.value })} />
              <Input label="Commodity value" type="number" step="0.01" testId="loan-commodity-value"
                value={form.commodity_value || ""} onChange={(e) => setForm({ ...form, commodity_value: e.target.value })} />
            </div>
          )}

          <div className="pt-2">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
              Society file charge (optional)
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Charge type" testId="loan-file-charge-type"
                value={form.file_charge_type || ""} onChange={(e) => setForm({ ...form, file_charge_type: e.target.value })}>
                <option value="">— none —</option>
                <option value="percent">Percent of principal</option>
                <option value="flat">Flat rupees</option>
              </Select>
              <Input
                label={form.file_charge_type === "flat" ? "Amount (₹)" : "Percent"}
                type="number" step="0.01" testId="loan-file-charge-value"
                value={form.file_charge_value || ""} onChange={(e) => setForm({ ...form, file_charge_value: e.target.value })}
                disabled={!form.file_charge_type}
              />
            </div>
            {form.file_charge_type && form.principal && form.file_charge_value && (
              <div className="text-xs text-slate-500 mt-1 mono">
                Auto-charged on issue: {money(
                  form.file_charge_type === "percent"
                    ? Number(form.principal) * Number(form.file_charge_value) / 100
                    : Number(form.file_charge_value)
                )}
              </div>
            )}
          </div>

          <div className="pt-1 border-t border-slate-100">
            <Input label="Disbursement date (optional, for legacy)" type="date" testId="loan-disbursed-on"
              value={form.disbursed_on || ""} onChange={(e) => setForm({ ...form, disbursed_on: e.target.value })} />
            <div className="text-[11px] text-slate-500 mt-1">
              For old loans: enter the real disbursement date — the EMI schedule and due dates are built from this. Leave blank for a brand-new loan.
            </div>
          </div>

          <Input label="Notes" testId="loan-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          {err && <div className="text-sm text-rose-600">{String(err)}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid={TID.saveBtn}>Create loan</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
const typeLabel = (t) => ({ deposit: "Deposit-backed", general: "General", commodity: "Commodity" }[t] || t);
